import { Chip } from '@mui/material';
import type { ComplianceRequirementStatus } from '../../services/licenseComplianceService';

export type ProvenanceBadgeStatus = ComplianceRequirementStatus | 'no_license';

interface ProvenanceBadgeProps {
  status: ProvenanceBadgeStatus;
  /** Two-letter state code, used for the 'no_license' variant label. */
  stateCode?: string;
  size?: 'small' | 'medium';
}

/**
 * Provenance chip for per-license CPE requirement rows.
 * Communicates whether requirement values are system defaults, user-confirmed
 * defaults, user-modified custom values, or absent entirely.
 */
export default function ProvenanceBadge({ status, stateCode, size = 'small' }: ProvenanceBadgeProps) {
  switch (status) {
    case 'unconfirmed':
      return (
        <Chip
          label="Unconfirmed"
          size={size}
          sx={{ bgcolor: 'status.enrolled.bg', color: 'status.enrolled.fg' }}
        />
      );
    case 'modified':
      return (
        <Chip
          label="Custom requirements"
          size={size}
          sx={{ bgcolor: 'status.modified.bg', color: 'status.modified.fg' }}
        />
      );
    case 'confirmed':
      return (
        <Chip
          label="Defaults confirmed"
          size={size}
          sx={{ bgcolor: 'status.completed.bg', color: 'status.completed.fg' }}
        />
      );
    case 'no_license':
      return (
        <Chip
          label={stateCode ? `No ${stateCode} license` : 'No license'}
          size={size}
          variant="outlined"
          sx={{ color: 'text.disabled', borderColor: 'divider' }}
        />
      );
    case 'not_set_up':
    default:
      return (
        <Chip
          label="Not set up"
          size={size}
          variant="outlined"
          sx={{ color: 'text.secondary', borderColor: 'divider' }}
        />
      );
  }
}
